/**
 * The portal's WorkOS "Sign-in endpoint" (`/login`): what to do when WorkOS
 * sends a visitor to the app because a sign-in has to START in the app, e.g.
 * after an invitation is accepted or a password is reset on the hosted page.
 *
 * What the WorkOS docs say about it (checked 2026-11):
 *   - AuthKit redirects to the sign-in endpoint when a sign-in did not
 *     originate at the app; "password reset and invitation details are
 *     preserved through this redirect" as long as the endpoint starts an
 *     AuthKit sign-in rather than rendering its own page
 *     (https://workos.com/docs/authkit/vanilla/nodejs).
 *   - No query parameters are documented for those AuthKit flows. The only
 *     documented one is `connection_id`, appended for IdP-initiated SSO
 *     (https://workos.com/docs/sso/login-flows); authkit-js 0.20's `signIn`
 *     has no connection option, so it cannot be forwarded and is ignored.
 *
 * So the plain case is a bare `signIn()`. On top of it we forward the four
 * parameters that both the WorkOS authorize URL and authkit-js 0.20's
 * `signIn` know, if WorkOS (or a hand-built link) supplies them. `state` is
 * not forwarded: nothing in the portal reads it, and WorkOS does not document
 * sending one here. `screen_hint` is not accepted by `signIn` in 0.20.
 *
 * The pure decision lives here so it is unit-testable; `LoginRoute.tsx` only
 * wires it to AuthKit and the router.
 */

/** The subset of authkit-js 0.20 `signIn` options this endpoint forwards. */
export type LoginSignInOptions = {
  invitationToken?: string;
  passwordResetToken?: string;
  loginHint?: string;
  organizationId?: string;
};

export type LoginAction =
  /** AuthKit is still starting; its real `signIn` is not bound yet. */
  | { kind: 'wait' }
  /** Already signed in and nothing to redeem: go to the portal root. */
  | { kind: 'home' }
  | { kind: 'sign-in'; options: LoginSignInOptions };

/** Path of the endpoint; registered as the application's Sign-in endpoint in WorkOS. */
export const LOGIN_PATH = '/login';

const MAX_LEN = 512;
/** Invitation / reset tokens and org ids: printable ASCII, no spaces. */
const OPAQUE = /^[\x21-\x7e]+$/;
const ORG_ID = /^org_[A-Za-z0-9]{1,64}$/;
const CONTROL = /[\x00-\x1f\x7f]/;

function param(params: URLSearchParams, name: string): string | undefined {
  const v = params.get(name)?.trim();
  return v ? v : undefined;
}

/**
 * Parameters worth forwarding, each dropped (never repaired) when malformed:
 * a bad value degrades to a plain sign-in, which WorkOS still completes.
 */
export function parseLoginParams(search: string | URLSearchParams): LoginSignInOptions {
  const params = typeof search === 'string' ? new URLSearchParams(search) : search;
  const out: LoginSignInOptions = {};

  const invitation = param(params, 'invitation_token');
  if (invitation && invitation.length <= MAX_LEN && OPAQUE.test(invitation)) {
    out.invitationToken = invitation;
  }
  const reset = param(params, 'password_reset_token');
  if (reset && reset.length <= MAX_LEN && OPAQUE.test(reset)) {
    out.passwordResetToken = reset;
  }
  const hint = param(params, 'login_hint');
  if (hint && hint.length <= 320 && !CONTROL.test(hint)) {
    out.loginHint = hint;
  }
  const org = param(params, 'organization_id');
  if (org && ORG_ID.test(org)) {
    out.organizationId = org;
  }
  return out;
}

export function resolveLoginAction(input: {
  isLoading: boolean;
  signedIn: boolean;
  search: string | URLSearchParams;
}): LoginAction {
  if (input.isLoading) return { kind: 'wait' };
  const options = parseLoginParams(input.search);
  const hasToken = options.invitationToken !== undefined || options.passwordResetToken !== undefined;
  if (input.signedIn && !hasToken) return { kind: 'home' };
  return { kind: 'sign-in', options };
}
